import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { DadosAgrupamento } from '@home/submodulos/dados-meteorologicos/interfaces/agrupamento';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	PeriodosLabel,
} from '@home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';
import { RelatoriosService } from '@home/submodulos/dados-meteorologicos/services/relatorios.service';
import * as Highcharts from 'highcharts/highstock';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { RelatorioNDCResponse } from './../../../interfaces/relatorio-ndc';
import { options } from './chartOptions';
import {
	COR_REGIAO_INDIVIDUAL,
	corrigeDuplicacaoNome,
	gerarFilenameGrafico,
} from '../../../utils';
import { DateTimeUtils } from '@utils/datetime-util';
import * as pdfseira from '@utils/pdf-seira';
import { DocumentExporter } from '@utils/document-exporter';
import * as ExcelTable from 'mr-excel';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import {
	alinharDado,
	baixarEAbrirPDF,
	criarImagemBase64FromChart,
	enumAsSelectOptions,
	formatarDataPadraoMesAno,
	isNuloOuUndefined,
	numberToBrNumberTables,
	setAgrupamentoEspecial,
} from '@utils';
import { Select } from '@layout/interfaces/select';
import format from 'date-fns/format';
import { AgrupamentoEspecial } from '../../../interfaces';
import { colors } from '@shared/interfaces/paleta-cores';
import { NumberUtils } from '@utils/number-utils';
import {
	getCorPorRegiao,
	ordenarRegioesPluviometricas,
} from '../../../utils/grafico-regioes-utils';
import { isEstadoCompleto } from '../../../utils/agrupamento-utils';
import { obterLabelPeriodo } from '../../../utils';
const labelColunaPrecipitacao = 'Pluviometria observada (mm)';
const labelColunaNDC = 'Número de dias com chuva';
@Component({
	selector: 'seira-grafico-ndc',
	templateUrl: './grafico-ndc.component.html',
	styleUrls: ['./grafico-ndc.component.scss'],
})
export class GraficoNdcComponent implements OnInit, OnDestroy {
	chart!: Highcharts.Chart;
	chartOptions: Highcharts.Options = {
		...options,
		legend: {
			enabled: true,
			layout: 'horizontal',
			align: 'center',
			verticalAlign: 'bottom',
			itemStyle: {
				fontWeight: 'normal',
				fontSize: '12px',
			},
		},
	};

	inputs = inject(INPUTS_RELATORIOS);
	_destroyed = new Subject();

	tituloEixoX = '';
	resultados: RelatorioNDCResponse[] = [];
	descricaoRelatorio =
		'O NDC, Número de dias com chuva, representa o número de dias em que a pluviometria diária, acumulada em 24 horas, foi igual ou superior a 0,1 mm.';

	periodoTitulo = '';
	loading = true;

	tituloAgrupamento = '';
	agrupamento: Select[] | any = [];

	tipoPDF = false;

	botoesDeExportacao: GroupButton[] = [
		{
			label: '.png',
			size: 'small',
			icon: 'ph-image',
			onClick: () => this.exportarPNG(),
		},

		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () => {
				this.exportarPDF().then();
			},
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => this.exportarCSV(),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => this.exportarTXT(),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];

	get _agrupamento(): string {
		return this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value;
	}

	constructor(
		private relatoriosService: RelatoriosService,
		private toastrService: ToastrService
	) {
		this.agrupamento = enumAsSelectOptions(AgrupamentoEspecial);
	}

	ngOnInit(): void {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValue(this.agrupamento[0].value);
		this.inputs.form
			.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
			?.setValue(this.inputs.regioes[0].value);
		this.setValidatorsByAgrupamento(
			this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value
		);
		this.setValidators();
	}

	ngOnDestroy() {
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.ESTACAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MUNICIPIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MICRORREGIAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MESORREGIAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.BACIA)?.clearValidators();
		this.inputs.form
			.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
			?.clearValidators();
		this._destroyed.next(undefined);
	}

	setValidatorsByAgrupamento(agrupamentoValue: string) {
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.MUNICIPIO);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.ESTACAO);
		this.handleRemoveValidatorsFromFormControl(
			FormularioRelatorio.MICRORREGIAO
		);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.MESORREGIAO);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.BACIA);
		this.handleRemoveValidatorsFromFormControl(
			FormularioRelatorio.REGIAO_PLUVIOMETRICA
		);

		switch (agrupamentoValue) {
			case 'MUNICIPIO_POSTO':
				this.inputs.form
					?.get(FormularioRelatorio.ESTACAO)
					?.setValidators(Validators.required);
				break;
			case 'MICRORREGIAO':
				this.inputs.form
					?.get(FormularioRelatorio.MICRORREGIAO)
					?.setValue('0', { emitEvent: false });
				this.inputs.form
					?.get(FormularioRelatorio.MICRORREGIAO)
					?.setValidators(Validators.required);
				break;
			case 'MESORREGIAO':
				this.inputs.form
					?.get(FormularioRelatorio.MESORREGIAO)
					?.setValue('0', { emitEvent: false });
				this.inputs.form
					?.get(FormularioRelatorio.MESORREGIAO)
					?.setValidators(Validators.required);
				break;
			case 'BACIA_SUBBACIA':
				this.inputs.form
					?.get(FormularioRelatorio.BACIA)
					?.setValue('0', { emitEvent: false });
				this.inputs.form
					?.get(FormularioRelatorio.BACIA)
					?.setValidators(Validators.required);
				break;
			case 'MUNICIPIO':
				this.inputs.form
					?.get(FormularioRelatorio.MUNICIPIO)
					?.setValidators(Validators.required);
				break;
			case 'REGIAO_PLUVIOMETRICA':
				this.inputs.form
					?.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
					?.setValue('0', { emitEvent: false });
				this.inputs.form
					?.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
					?.setValidators(Validators.required);
				break;
		}
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: value => {
					this.setValidatorsByAgrupamento(value);
				},
			});

		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
	}

	setChart(chart: Highcharts.Chart) {
		this.chart = chart;
		this.atualizarTitulosChart();

		const larguraAtual = this.chart.chartWidth;
		const alturaAtual = this.chart.chartHeight;

		this.chartOptions = {
			...this.chartOptions,
			exporting: {
				...this.chartOptions.exporting,
				sourceWidth: larguraAtual,
				sourceHeight: alturaAtual,
				scale: 1,
				chartOptions: {
					chart: {
						width: larguraAtual,
						height: alturaAtual,
					},
				},
			},
		};
		this.configurarChart(this.resultados);
	}

	getDataFromForm(data: FormularioRelatorio) {
		return this.inputs.form.get(data);
	}

	getNomeRegiaoPluviometricaSelecionada() {
		const regiaoSelecionada = this.getDataFromForm(
			FormularioRelatorio.REGIAO_PLUVIOMETRICA
		)?.value;

		return this.inputs.regioes.find(
			regiao => regiao.value?.toString() === regiaoSelecionada?.toString()
		)?.name;
	}

	getNomeMesorregiaoSelecionada() {
		const mesorregiaoSelecionada = this.getDataFromForm(
			FormularioRelatorio.MESORREGIAO
		)?.value;

		return this.inputs.mesorregioes.find(
			mesorregiao =>
				mesorregiao.value?.toString() === mesorregiaoSelecionada?.toString()
		)?.name;
	}

	getNomeBaciaSelecionada() {
		const baciaSelecionada = this.getDataFromForm(
			FormularioRelatorio.BACIA
		)?.value;

		return this.inputs.bacias.find(
			bacia => bacia.value?.toString() === baciaSelecionada?.toString()
		)?.name;
	}

	handleRemoveValidatorsFromFormControl(data: FormularioRelatorio) {
		this.inputs.form.get(data)?.clearValidators();
		this.inputs.form.get(data)?.updateValueAndValidity({ emitEvent: false });
	}

	get isRegiaoEstadoCompleto() {
		return (
			this._agrupamento === 'REGIAO_PLUVIOMETRICA' &&
			this.getDataFromForm(FormularioRelatorio.REGIAO_PLUVIOMETRICA)?.value ===
				'0'
		);
	}

	get isMicrorregiaoEstadoCompleto() {
		return (
			this._agrupamento === 'MICRORREGIAO' &&
			this.getDataFromForm(FormularioRelatorio.MICRORREGIAO)?.value === '0'
		);
	}

	get isMesorregiaoEstadoCompleto() {
		return (
			this._agrupamento === 'MESORREGIAO' &&
			this.getDataFromForm(FormularioRelatorio.MESORREGIAO)?.value === '0'
		);
	}

	get isBaciaEstadoCompleto() {
		return (
			this._agrupamento === 'BACIA_SUBBACIA' &&
			this.getDataFromForm(FormularioRelatorio.BACIA)?.value === '0'
		);
	}

	private getLabelAgrupamento() {
		if (
			this.isRegiaoEstadoCompleto ||
			this.isMicrorregiaoEstadoCompleto ||
			this.isMesorregiaoEstadoCompleto ||
			this.isBaciaEstadoCompleto
		) {
			return 'Estado completo';
		}

		return setAgrupamentoEspecial(this._agrupamento);
	}

	private getCorSerie(nomeSerie: string): string | undefined {
		if (!isEstadoCompleto(this.inputs.form, this._agrupamento)) {
			return COR_REGIAO_INDIVIDUAL;
		}
		if (this._agrupamento === 'MESORREGIAO') {
			return getCorPorRegiao(
				nomeSerie,
				this.inputs.mesorregioes,
				this._agrupamento
			);
		}
		if (this._agrupamento === 'REGIAO_PLUVIOMETRICA') {
			return getCorPorRegiao(nomeSerie, this.inputs.regioes, this._agrupamento);
		}

		return undefined;
	}

	gerarRelatorio() {
		this.limparChart();

		const diaInicio = this.getDataFromForm(
			FormularioRelatorio.DATA_INICIO
		)?.value;
		const diaFim = this.getDataFromForm(FormularioRelatorio.DATA_FIM)?.value;
		const agrupamento = this.getDataFromForm(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;
		const periodoAgrupamento = this.getDataFromForm(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value as PeriodosLabel;

		if (
			!diaInicio ||
			!diaFim ||
			!agrupamento ||
			!periodoAgrupamento ||
			this.inputs.form.invalid
		) {
			return;
		}

		this.loading = true;
		this.inputs.setLoading(true);
		const periodoFormatado = DateTimeUtils.formatarDataPeriodo(
			diaInicio,
			diaFim,
			periodoAgrupamento
		);
		const labelPeriodo = obterLabelPeriodo(periodoAgrupamento);

		this.periodoTitulo = `${labelPeriodo} (${periodoFormatado})`;

		const dados: DadosAgrupamento = {
			diaInicio,
			diaFim,
			agrupamento,
			periodoAgrupamento: periodoAgrupamento.toUpperCase(),
			isRetornoAgrupado: agrupamento !== 'REGIAO_PLUVIOMETRICA',
		};

		switch (agrupamento) {
			case 'MUNICIPIO_POSTO':
				dados.posto = this.getDataFromForm(FormularioRelatorio.ESTACAO)?.value;
				break;
			case 'MICRORREGIAO':
				const microrregiao = this.getDataFromForm(
					FormularioRelatorio.MICRORREGIAO
				)?.value;
				dados.microrregiao = microrregiao === '0' ? 0 : microrregiao;
				dados.isRetornoAgrupado = microrregiao === '0';
				break;
			case 'MESORREGIAO':
				const mesorregiao = this.getDataFromForm(
					FormularioRelatorio.MESORREGIAO
				)?.value;
				dados.mesorregiao = mesorregiao === '0' ? undefined : mesorregiao;
				dados.isRetornoAgrupado = mesorregiao === '0';
				break;
			case 'BACIA_SUBBACIA':
				const bacia = this.getDataFromForm(FormularioRelatorio.BACIA)?.value;
				dados.bacia = bacia === '0' ? undefined : bacia;
				dados.isRetornoAgrupado = bacia === '0';
				break;
			case 'MUNICIPIO':
				dados.municipio = this.getDataFromForm(
					FormularioRelatorio.MUNICIPIO
				)?.value;
				break;
			case 'REGIAO_PLUVIOMETRICA':
				const regiao = this.getDataFromForm(
					FormularioRelatorio.REGIAO_PLUVIOMETRICA
				)?.value;
				dados.regiao = regiao === '0' ? undefined : regiao;
				dados.isRetornoAgrupado = regiao === '0';
				break;
		}

		this.relatoriosService.buscarRelatorioNDCPorAgrupamento(dados).subscribe({
			next: ndcAgrupados => {
				const nomeRegiaoSelecionada =
					agrupamento === 'REGIAO_PLUVIOMETRICA' && dados.regiao
						? this.getNomeRegiaoPluviometricaSelecionada()
						: undefined;
				const nomeMesorregiaoSelecionada =
					agrupamento === 'MESORREGIAO' && dados.mesorregiao
						? this.getNomeMesorregiaoSelecionada()
						: undefined;
				const nomeBaciaSelecionada =
					agrupamento === 'BACIA_SUBBACIA' && dados.bacia
						? this.getNomeBaciaSelecionada()
						: undefined;
				const resultadosFiltrados = nomeRegiaoSelecionada
					? ndcAgrupados.filter(
							item =>
								corrigeDuplicacaoNome(item.nome) ===
								corrigeDuplicacaoNome(nomeRegiaoSelecionada)
						)
					: nomeMesorregiaoSelecionada
						? ndcAgrupados.filter(
								item =>
									corrigeDuplicacaoNome(item.nome) ===
									corrigeDuplicacaoNome(nomeMesorregiaoSelecionada)
							)
						: nomeBaciaSelecionada
							? ndcAgrupados.filter(
									item =>
										corrigeDuplicacaoNome(item.nome) ===
										corrigeDuplicacaoNome(nomeBaciaSelecionada)
								)
							: ndcAgrupados;

				this.relatoriosService.atualizaVisibilidadeBotoesExportacao(
					resultadosFiltrados
				);
				this.resultados = resultadosFiltrados;

				this.chartOptions = {
					...this.chartOptions,
					title: {
						...this.chartOptions.title,
						text: `Número de dias com chuva (NDC) - ${this.periodoTitulo}`,
					},
					exporting: {
						enabled: false,
						buttons: {
							contextButton: {
								enabled: false,
							},
						},
						filename: gerarFilenameGrafico(
							`grafico_numero_dias_com_chuva_${this.inputs.form
								.get(FormularioRelatorio.AGRUPAMENTO)
								?.value.toLocaleLowerCase()}_${periodoAgrupamento.toLocaleLowerCase()}`
						),
					},
				};
			},
			error: () => {
				this.toastrService.error(
					'Erro ao gerar gráfico de NDC, tente novamente'
				);
				this.loading = false;
				this.inputs.setLoading(false);
				this.configurarChart([]);
			},
			complete: () => {
				this.loading = false;
				this.inputs.setLoading(false);
			},
		});
	}

	limparChart() {
		if (this.chart && this.chart.series.length > 0) {
			while (this.chart.series.length > 0) {
				this.chart.series[0].remove(true);
			}
		}
	}

	extrairNomes(preciptacoesAgrupadas: RelatorioNDCResponse[]) {
		return [...new Set(preciptacoesAgrupadas.map(el => el.nome).sort())];
	}

	configurarChart(resultados: RelatorioNDCResponse[]) {
		const diaInicio = this.getDataFromForm(
			FormularioRelatorio.DATA_INICIO
		)?.value;
		const diaFim = this.getDataFromForm(FormularioRelatorio.DATA_FIM)?.value;
		let series = [];
		let categorias: string[] = [];
		const tipoPeriodo = this.getDataFromForm(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value;

		categorias = [
			...new Set(
				resultados.map(resultado =>
					tipoPeriodo === 'anual' || tipoPeriodo === 'anual_parcial'
						? resultado.ano?.toString() || ''
						: tipoPeriodo === 'mensal'
							? `${String(resultado.mes?.toString().padStart(2, '0'))}/${
									resultado.ano || ''
								}`
							: `${format(diaInicio, 'dd/MM/yyyy')} a ${format(
									diaFim,
									'dd/MM/yyyy'
								)}`
				)
			),
		];

		let nomes = this.extrairNomes(resultados);

		if (this._agrupamento === 'REGIAO_PLUVIOMETRICA') {
			nomes = ordenarRegioesPluviometricas(nomes);
		}

		series = nomes.map(nome => ({
			type: 'column' as const,
			name: corrigeDuplicacaoNome(nome),
			color: this.getCorSerie(corrigeDuplicacaoNome(nome)),
			data: categorias.map(categoria => {
				const ponto = resultados.find(r => {
					const categoriaAtual =
						tipoPeriodo === 'anual' || tipoPeriodo === 'anual_parcial'
							? r.ano?.toString() || ''
							: tipoPeriodo === 'mensal'
								? `${String(r.mes?.toString().padStart(2, '0'))}/${r.ano || ''}`
								: `${format(diaInicio, 'dd/MM/yyyy')} a ${format(
										diaFim,
										'dd/MM/yyyy'
									)}`;

					return r.nome === nome && categoriaAtual === categoria;
				});
				return ponto
					? {
							name: corrigeDuplicacaoNome(ponto.nome),
							y: ponto.diasComChuva,
							precipitacao:
								ponto.somaPrecipitacao?.toFixed(1).replace('.', ',') || '0',
						}
					: { y: null, precipitacao: '0' };
			}),
		}));

		this.chart?.xAxis[0].setCategories(categorias);

		this.chart?.update({
			tooltip: {
				formatter: function () {
					const yValue = this.point.y;
					const precipitacaoValue = (this.point as any).precipitacao;
					return (
						`Dias com chuva: <b>${yValue} ${
							yValue === 1 ? 'dia' : 'dias'
						}</b><br>` +
						`Precipitação acumulada (mm): <b>${precipitacaoValue} mm</b><br>`
					);
				},
			},
		});

		while (this.chart?.series.length) {
			this.chart.series[0].remove(false);
		}

		series.forEach(serie => {
			this.chart?.addSeries(serie as Highcharts.SeriesOptionsType, false);
		});

		this.chart?.redraw();
	}

	private getChartColors(totalSeries: number): string[] {
		if (this.isMicrorregiaoEstadoCompleto || this.isBaciaEstadoCompleto) {
			return this.expandirPaleta(totalSeries, colors);
		}

		if (this.isMesorregiaoEstadoCompleto) {
			return this.expandirPaleta(totalSeries, colors);
		}

		return this.expandirPaleta(totalSeries, colors);
	}

	private expandirPaleta(totalSeries: number, paletaBase: string[]): string[] {
		if (totalSeries <= paletaBase.length) {
			return paletaBase.slice(0, totalSeries);
		}

		const paletaExpandida = [...paletaBase];

		for (let index = paletaBase.length; index < totalSeries; index++) {
			const hue = Math.round((360 / totalSeries) * index);
			paletaExpandida.push(`hsl(${hue}, 65%, 48%)`);
		}

		return paletaExpandida;
	}

	atualizarTitulosChart() {
		const agrupamento = this.getDataFromForm(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;
		const tipoPeriodo = this.getDataFromForm(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value;

		if (tipoPeriodo === 'mensal') {
			this.tituloEixoX = 'Meses';
		} else if (tipoPeriodo === 'anual') {
			this.tituloEixoX = 'Anos';
		} else {
			switch (agrupamento) {
				case 'MUNICIPIO_POSTO':
					this.tituloEixoX = 'Município/Posto';
					break;
				case 'MICRORREGIAO':
					this.tituloEixoX = 'Microrregião';
					break;
				case 'MUNICIPIO':
					this.tituloEixoX = 'Município';
					break;
				case 'MESORREGIAO':
					this.tituloEixoX = 'Mesorregiões';
					break;
				case 'REGIAO_PLUVIOMETRICA':
					this.tituloEixoX = 'Regiões Pluviométricas';
					break;
				case 'BACIA_SUBBACIA':
					this.tituloEixoX = 'Bacias e Sub-bacias';
					break;
			}
		}

		this.chart.xAxis[0].setTitle({ text: this.tituloEixoX });
	}

	getXLSXHeaders() {
		let headers = [];
		this.tituloAgrupamento = this.getLabelAgrupamento();
		const periodoAgrupamento = this.getDataFromForm(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value as PeriodosLabel;

		periodoAgrupamento != 'diario'
			? periodoAgrupamento === 'mensal'
				? (headers = [
						{ label: 'nome', text: this.tituloAgrupamento, size: 24 },
						{ label: 'mes', text: 'Data', size: 24 },
						{ label: 'diasComChuva', text: 'Dias com chuva', size: 24 },
						{
							label: 'somaPrecipitacao',
							text: 'Pluviometria observada (mm)',
							size: 48,
						},
					])
				: (headers = [
						{ label: 'nome', text: this.tituloAgrupamento, size: 24 },
						{ label: 'ano', text: 'Data', size: 24 },
						{ label: 'diasComChuva', text: 'Dias com chuva', size: 24 },
						{
							label: 'somaPrecipitacao',
							text: 'Pluviometria observada (mm)',
							size: 24,
						},
					])
			: (headers = [
					{ label: 'nome', text: this.tituloAgrupamento, size: 24 },
					{ label: 'diasComChuva', text: 'Dias com chuva', size: 24 },
					{
						label: 'somaPrecipitacao',
						text: 'Pluviometria observada (mm)',
						size: 24,
					},
				]);

		return headers;
	}

	private getTableData() {
		const dadosTabela: Array<any[]> = [];
		const agrupamento = this.inputs.form.get('agrupamento')?.value;
		this.tituloAgrupamento = this.getLabelAgrupamento();
		const cabecalho: any[] = [];
		const periodoAgrupamento = this.getDataFromForm(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value as PeriodosLabel;

		if (this.tipoPDF) {
			if (periodoAgrupamento !== 'diario') {
				cabecalho.push(
					{
						text: this.tituloAgrupamento,
						fillColor: '#DCDCDC',
						alignment: 'center',
					},
					{
						text: 'Data',
						fillColor: '#DCDCDC',
						alignment: 'center',
					},
					{ text: labelColunaNDC, fillColor: '#DCDCDC', alignment: 'center' },
					{
						text: labelColunaPrecipitacao,
						fillColor: '#DCDCDC',
						alignment: 'center',
					}
				);
			} else {
				cabecalho.push(
					{
						text: this.tituloAgrupamento,
						fillColor: '#DCDCDC',
						alignment: 'center',
					},
					{ text: labelColunaNDC, fillColor: '#DCDCDC', alignment: 'center' },
					{
						text: labelColunaPrecipitacao,
						fillColor: '#DCDCDC',
						alignment: 'center',
					}
				);
			}
		} else {
			if (periodoAgrupamento !== 'diario') {
				cabecalho.push(
					this.tituloAgrupamento,
					'Data',
					labelColunaNDC,
					labelColunaPrecipitacao
				);
			} else {
				cabecalho.push(
					this.tituloAgrupamento,
					labelColunaNDC,
					labelColunaPrecipitacao
				);
			}
		}

		dadosTabela.push(cabecalho);
		let valores: any[];

		if (agrupamento === 'MUNICIPIO_POSTO') {
			if (periodoAgrupamento === 'diario') {
				valores = this.resultados?.map(value => [
					value.nome && corrigeDuplicacaoNome(value.nome),
					alinharDado(value.diasComChuva, this.tipoPDF),
					alinharDado(
						numberToBrNumberTables(value.somaPrecipitacao, 1),
						this.tipoPDF
					),
				]);
			} else {
				valores = this.resultados?.map(value => [
					value.nome && corrigeDuplicacaoNome(value.nome),
					alinharDado(
						value.mes
							? formatarDataPadraoMesAno(value.mes!, value.ano!)
							: value.ano,
						this.tipoPDF
					),
					alinharDado(value.diasComChuva, this.tipoPDF),
					alinharDado(
						numberToBrNumberTables(value.somaPrecipitacao, 1),
						this.tipoPDF
					),
				]);
			}
		} else {
			if (periodoAgrupamento === 'diario') {
				valores = this.resultados?.map(value => [
					alinharDado(corrigeDuplicacaoNome(value.nome), this.tipoPDF),
					alinharDado(value.diasComChuva, this.tipoPDF),
					alinharDado(
						numberToBrNumberTables(value.somaPrecipitacao, 1),
						this.tipoPDF
					),
				]);
			} else {
				valores = this.resultados?.map(value => [
					alinharDado(corrigeDuplicacaoNome(value.nome), this.tipoPDF),
					alinharDado(
						value.mes
							? formatarDataPadraoMesAno(value.mes!, value.ano!)
							: value.ano,
						this.tipoPDF
					),
					alinharDado(value.diasComChuva, this.tipoPDF),
					alinharDado(
						numberToBrNumberTables(value.somaPrecipitacao, 1),
						this.tipoPDF
					),
				]);
			}
		}

		dadosTabela.push(...valores);
		return dadosTabela;
	}

	retornarNdcParaXLSX() {
		const tableData: any[] = [];
		this.tituloAgrupamento = this.getLabelAgrupamento();
		const periodoAgrupamento = this.getDataFromForm(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value as PeriodosLabel;

		periodoAgrupamento != 'diario'
			? periodoAgrupamento === 'mensal'
				? this.resultados.map(ndc => {
						tableData.push({
							nome: ndc.nome && corrigeDuplicacaoNome(ndc.nome),
							mes: `${ndc.mes! > 9 ? ndc.mes : '0' + ndc.mes}/${ndc.ano}`,
							diasComChuva: ndc.diasComChuva,
							somaPrecipitacao: isNuloOuUndefined(ndc.somaPrecipitacao)
								? '-'
								: NumberUtils.getIgnorandoCasasDecimais(ndc.somaPrecipitacao),
						});
					})
				: this.resultados.map(ndc => {
						tableData.push({
							nome: ndc.nome && corrigeDuplicacaoNome(ndc.nome),
							ano: ndc.ano,
							diasComChuva: ndc.diasComChuva,
							somaPrecipitacao: isNuloOuUndefined(ndc.somaPrecipitacao)
								? '-'
								: NumberUtils.getIgnorandoCasasDecimais(ndc.somaPrecipitacao),
						});
					})
			: this.resultados.map(ndc => {
					tableData.push({
						nome: ndc.nome && corrigeDuplicacaoNome(ndc.nome),
						diasComChuva: ndc.diasComChuva,
						somaPrecipitacao: isNuloOuUndefined(ndc.somaPrecipitacao)
							? '-'
							: NumberUtils.getIgnorandoCasasDecimais(ndc.somaPrecipitacao),
					});
				});

		return tableData;
	}
	private carregarImagem(src: string): Promise<HTMLImageElement> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => resolve(img);
			img.onerror = err => reject(err);
			img.src = src;
		});
	}

	private async getLogoBase64(caminho: string): Promise<string | null> {
		try {
			const logo = await this.carregarImagem(caminho);

			const canvas = document.createElement('canvas');
			canvas.width = logo.width;
			canvas.height = logo.height;

			const ctx = canvas.getContext('2d')!;
			ctx.fillStyle = '#FFFFFF';
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(logo, 0, 0, canvas.width, canvas.height);

			return canvas.toDataURL('image/png', 1);
		} catch (e) {
			console.error(`[NDC] Erro ao carregar logo ${caminho}:`, e);
			return null;
		}
	}

	private async getLogoAesaBase64(): Promise<string> {
		return (await this.getLogoBase64('assets/images/logos/logoAesa.svg')) ?? '';
	}

	private async getLogoGovernoBase64(): Promise<string> {
		return (
			(await this.getLogoBase64('assets/images/logos/governo_pb.png')) ?? ''
		);
	}

	private downloadDataUrl(nomeArquivo: string, dataUrl: string) {
		const link = document.createElement('a');
		link.href = dataUrl;
		link.download = nomeArquivo;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	async exportarPDF() {
		const periodoAgrupamento = this.getDataFromForm(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value as PeriodosLabel;

		this.tipoPDF = true;

		const documentDefinition: any =
			await pdfseira.documentDefinitions('landscape');

		documentDefinition.pageOrientation = 'landscape';

		const logoAesaBase64 = await this.getLogoAesaBase64();
		const logoGovernoBase64 = await this.getLogoGovernoBase64();

		if (logoAesaBase64 || logoGovernoBase64) {
			documentDefinition.images = documentDefinition.images || {};

			if (logoAesaBase64) {
				documentDefinition.images['aesaLogo'] = logoAesaBase64;
			}

			if (logoGovernoBase64) {
				documentDefinition.images['governoLogo'] = logoGovernoBase64;
			}

			documentDefinition.footer = (currentPage: number, pageCount: number) => ({
				margin: [40, 0, 40, 20],
				columns: [
					{
						image: 'aesaLogo',
						width: 70,
						alignment: 'left',
					},
					{
						text: `Página ${currentPage} de ${pageCount}`,
						alignment: 'center',
						fontSize: 8,
						width: '*',
					},
					{
						image: 'governoLogo',
						width: 70,
						alignment: 'right',
					},
				],
			});

			documentDefinition.content.push([
				{
					image: await criarImagemBase64FromChart(this.chart),
					width: 750,
					height: 400,
					margin: [0, 45, 5, 15],
					alignment: 'center',
				},
			]);

			documentDefinition.content.push({
				table: {
					widths:
						periodoAgrupamento != 'diario'
							? ['25%', '25%', '25%', '25%']
							: ['35%', '35%', '30%'],
					body: this.getTableData(),
					layout: {
						noWrap: false,
						fontSize: 5,
					},
				},
				marginLeft: 20,
			});

			baixarEAbrirPDF(
				`Número de dias com chuva (NDC) - ${this.periodoTitulo}`,
				documentDefinition
			);
			return;
		}

		documentDefinition.footer = (currentPage: number, pageCount: number) => {
			return {
				margin: [40, 0, 40, 10],
				columns: [
					{
						text: `Página ${currentPage} de ${pageCount}`,
						alignment: 'left',
						fontSize: 8,
					},
					{
						image: logoAesaBase64,
						width: 60,
						alignment: 'right',
					},
				],
			};
		};

		documentDefinition.content.push([
			{
				text: `Número de dias com chuva (NDC) - ${this.periodoTitulo}`,
				alignment: 'center',
				margin: [0, 15, 5, 15],
			},
			{
				image: await criarImagemBase64FromChart(this.chart),
				width: 750,
				height: 400,
				margin: [0, 15, 5, 15],
				alignment: 'center',
			},
		]);

		documentDefinition.content.push({
			table: {
				widths:
					periodoAgrupamento != 'diario'
						? ['25%', '25%', '25%', '25%']
						: ['35%', '35%', '30%'],
				body: this.getTableData(),
				layout: {
					noWrap: false,
					fontSize: 5,
				},
			},
			marginLeft: 20,
		});

		baixarEAbrirPDF(
			`Número de dias com chuva (NDC) - ${this.periodoTitulo}`,
			documentDefinition
		);
	}

	exportarCSV() {
		this.tipoPDF = false;
		const tableData = this.getTableData();

		DocumentExporter.gerarCSV(
			tableData,
			`Número de dias com chuva (NDC) - ${this.periodoTitulo}`
		);
	}

	exportarTXT() {
		this.tipoPDF = false;
		const tableData = this.getTableData();
		let txtData = '';

		if (tableData.length > 0) {
			const colunas = tableData[0];

			tableData.slice(1).forEach(dadoRelatorio => {
				colunas.forEach((coluna: string, index: number) => {
					const valor = dadoRelatorio[index];
					txtData += `${coluna}: ${valor ?? '-'}\n`;
				});

				txtData += '\n';
			});
		}

		DocumentExporter.gerarTXT(
			txtData,
			`Número de dias com chuva (NDC) - ${this.periodoTitulo}`
		);
	}

	exportarXLSX() {
		const agrupamento = this.inputs.form.get('agrupamento')?.value;
		this.tituloAgrupamento = setAgrupamentoEspecial(agrupamento);
		const nomeArquivo = `Número de dias com chuva (NDC) - ${this.periodoTitulo}`;
		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};
		const rowStyle = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const rowStyleCenter = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const headers = this.getXLSXHeaders();
		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyle,
				rowStyleCenter,
				title,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition(
						data: any,
						object: any,
						rowIndex: number,
						colIndex: number,
						fromHeader: boolean,
						styleKeys: string[]
					) {
						if (data && data.label) {
							return 'headerStyleCenter';
						} else {
							if (colIndex === 0 && agrupamento === 'MUNICIPIO_POSTO') {
								return 'rowStyle';
							} else {
								return 'rowStyleCenter';
							}
						}
					},
					headers: headers,
					data: this.retornarNdcParaXLSX(),
					columns: [],
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: nomeArquivo,
						styleId: 'title',
					},
				},
			],
			fileName: nomeArquivo,
		};

		ExcelTable.generateExcel(dataExcel);
	}
	async exportarPNG() {
		if (!this.chart) {
			this.toastrService.error(
				'Não é possível exportar PNG pois nenhum gráfico foi gerado.'
			);
			return;
		}

		try {
			const chartBase64 = await criarImagemBase64FromChart(this.chart);
			const baseImg = await this.carregarImagem(chartBase64);

			const canvas = document.createElement('canvas');
			canvas.width = baseImg.width;
			canvas.height = baseImg.height;
			const ctx = canvas.getContext('2d')!;

			ctx.fillStyle = '#FFFFFF';
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(baseImg, 0, 0, canvas.width, canvas.height);

			try {
				const margem = canvas.width * 0.02;
				const larguraLogoAesa = canvas.width * 0.08;
				const larguraLogoGoverno = canvas.width * 0.1;

				const logoAesa = await this.carregarImagem(
					'assets/images/logos/logoAesa.svg'
				);
				const alturaLogoAesa =
					(larguraLogoAesa / logoAesa.width) * logoAesa.height;
				const xLogoAesa = margem;
				const yLogoAesa = canvas.height - alturaLogoAesa - margem;

				ctx.drawImage(
					logoAesa,
					xLogoAesa,
					yLogoAesa,
					larguraLogoAesa,
					alturaLogoAesa
				);

				const logoGoverno = await this.carregarImagem(
					'assets/images/logos/governo_pb.png'
				);
				const alturaLogoGoverno =
					(larguraLogoGoverno / logoGoverno.width) * logoGoverno.height;
				const xLogoGoverno = canvas.width - larguraLogoGoverno - margem;
				const yLogoGoverno = canvas.height - alturaLogoGoverno - margem;

				ctx.drawImage(
					logoGoverno,
					xLogoGoverno,
					yLogoGoverno,
					larguraLogoGoverno,
					alturaLogoGoverno
				);
			} catch (e) {
				console.error('[NDC PNG] Erro ao desenhar as logos:', e);
			}

			const dataUrl = canvas.toDataURL('image/png', 1);
			const nomeArquivo = `Número de dias com chuva (NDC) - ${
				this.periodoTitulo || 'NDC'
			}.png`;
			this.downloadDataUrl(nomeArquivo, dataUrl);
		} catch (e) {
			console.error('[NDC PNG] Erro ao exportar PNG:', e);
			this.toastrService.error('Falha ao exportar PNG.');
		}
	}
}
